import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import CineUploadCanvas from "./CineUploadCanvas";
import { setCinematicPhase, resetCinematicUpload } from "../../../Reducers/uiReducer";
import { useTranslation } from "react-i18next";
import { VERDICTIA_MARK_SRC } from "../../../brand/verdictiaBrand";

// Overlay global plein écran, fond blanc, header visible dessous.
// Le canvas gère toutes les phases (enter → scan → check → exit) et signale la fin.
// Le rideau se lance UNIQUEMENT quand le canvas a fini son animation de sortie.

const Curtain = ({ onDone }) => {
  const [run, setRun] = useState(false);
  const curtainRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setRun(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const el = curtainRef.current;
    if (!el) return;
    const onEnd = () => onDone?.();
    el.addEventListener('animationend', onEnd);
    return () => el.removeEventListener('animationend', onEnd);
  }, [onDone]);

  return (
    <div ref={curtainRef} className={"cine-curtain" + (run ? " cine-curtain--run" : "") } />
  );
};

export default function CineUploadOverlay() {
  const { t } = useTranslation('review');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cinematic = useSelector(s => s.ui?.cinematicUpload) || { active: false, phase: 'idle', fileName: '', startedAt: Date.now(), targetAnalyseId: null, metadataDraft: null };
  const { active, phase, fileName, startedAt, targetAnalyseId, metadataDraft } = cinematic;

  const isOnCreatePage = location.pathname.endsWith('/create');
  const backendReady = active && !!targetAnalyseId;

  const [canvasBox, setCanvasBox] = useState({ w: 420, h: 300 });
  const [isCheckUnlocked, setIsCheckUnlocked] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const hasNavigatedRef = useRef(false);
  const pendingResetAfterNavRef = useRef(false);

  useEffect(() => {
    function onResize() {
      const isMobileViewport = window.innerWidth < 640;
      if (isMobileViewport) {
        const w = Math.max(240, Math.min(window.innerWidth - 88, 310));
        const h = Math.max(180, Math.min(window.innerHeight * 0.32, 230));
        setCanvasBox({ w, h });
        return;
      }

      const vw = Math.min(window.innerWidth, 960);
      const vh = Math.min(window.innerHeight - 140, 520);
      const w = Math.max(360, Math.min(540, vw * 0.7));
      const h = Math.max(260, Math.min(360, vh * 0.68));
      setCanvasBox({ w, h });
    }
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Laisse un petit temps pour l'animation du contrat avant le check.
   const EXTRA_CONTRACT_ANIM_MS = 800;

  useEffect(() => {
    if (!active || !backendReady) {
      setIsCheckUnlocked(false);
      return;
    }

    const elapsed = Math.max(0, Date.now() - (startedAt || Date.now()));
    const remaining = Math.max(0, EXTRA_CONTRACT_ANIM_MS - elapsed);
    const timer = setTimeout(() => setIsCheckUnlocked(true), remaining);
    return () => clearTimeout(timer);
  }, [active, backendReady, startedAt]);

  const canShowCheck = backendReady && isCheckUnlocked;

  // Callback du canvas: l'animation de sortie est terminée → rideau
  const handleExitComplete = useCallback(() => {
    if (phase !== 'curtain') {
      dispatch(setCinematicPhase('curtain'));
    }
  }, [phase, dispatch]);

   // Quand rideau terminé => naviger vers la nouvelle page et reset overlay
   const handleCurtainDone = () => {
     if (targetAnalyseId && isOnCreatePage && !hasNavigatedRef.current) {
       hasNavigatedRef.current = true;
       pendingResetAfterNavRef.current = true;
       navigate(`/stage-review/${targetAnalyseId}`, {
         replace: true,
         state: { metadataDraft, fromCinematicUpload: true }
       });
       return;
     }
     // Laisser le rideau visible plus longtemps pour que l'effet visuel soit complet
     // avant de retirer le composant du DOM
     setTimeout(() => {
       setShouldRender(false);
       dispatch(resetCinematicUpload());
     }, 300);
   };

  // Une fois la navigation effective, on retire l'overlay avec un léger délai
  useEffect(() => {
    if (!active || !pendingResetAfterNavRef.current || !targetAnalyseId) return;
    const expectedPath = `/stage-review/${targetAnalyseId}`;
    if (location.pathname !== expectedPath) return;

    const t = setTimeout(() => {
      setShouldRender(false);
      pendingResetAfterNavRef.current = false;
      dispatch(resetCinematicUpload());
    }, 120);
    return () => clearTimeout(t);
  }, [active, targetAnalyseId, location.pathname, dispatch]);

  // Reset flags quand l'overlay est réactivé
  useEffect(() => {
    if (active) setShouldRender(true);
  }, [active]);

  if (!shouldRender) return null;

  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const showCurtain = phase === 'curtain' || (prefersReduced && backendReady);

  return (
    <div className="cine-overlay" role="status" aria-live="polite">
      {/* Coins décoratifs géométriques */}
      <div className="cine-deco-corner cine-deco-corner--tl" />
      <div className="cine-deco-corner cine-deco-corner--tr" />
      <div className="cine-deco-corner cine-deco-corner--bl" />
      <div className="cine-deco-corner cine-deco-corner--br" />

      {!showCurtain && (
        <div className="cine-box">
          {/* Branding Verdictia */}
          <div className="cine-brand">
            <img src={VERDICTIA_MARK_SRC} alt="" aria-hidden="true" />
            <span className="cine-brand-name">Verdictia</span>
          </div>

          <div className="cine-title">{t('stage.importing')}</div>

          <div className="cine-canvas-wrap">
            <CineUploadCanvas
              width={canvasBox.w}
              height={canvasBox.h}
              readyToComplete={canShowCheck}
              onExitComplete={handleExitComplete}
            />
          </div>

          <div className="cine-caption">
            {canShowCheck
              ? t('stage.cine_ready')
              : (t('stage.importing') + (fileName ? ` · ${fileName}` : ''))
            }
          </div>

          {/* Barre de progression animée */}
          {!canShowCheck && (
            <div className="cine-progress-track">
              <div className="cine-progress-bar" />
            </div>
          )}
        </div>
      )}
      {showCurtain && <Curtain onDone={handleCurtainDone} />}
    </div>
  );
}
